import { environment } from '@env/environment';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BridgeService } from '@app/services/bridge.service';
import { io, Socket } from 'socket.io-client';
import { feathers, Application, ServiceMethods } from '@feathersjs/feathers';
import socketIoClient from '@feathersjs/socketio-client';
import authenticationClient from '@feathersjs/authentication-client';

@Injectable({ providedIn: 'root' })
export class BackendService {
    public readonly bridgeService = inject(BridgeService);

    private readonly client: Application<any>;
    private readonly io: Socket;
    public lsIsAvailable: boolean;
    private connected: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
    public connected$: Observable<boolean> = this.connected.asObservable();

    constructor() {
        const opts = {
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            timeout: 20000,
            auth: {
                accessToken: localStorage.getItem('feathers-jwt') ?? undefined,
            },
        };

        this.lsIsAvailable = this.hasStorage();

        this.io = io(environment.url.backendApi, opts);
        this.io?.on('connect', () => {
            this.connected.next(true);
            console.log('BackendService - Connected:', this.io.id);
        });
        this.io?.on('disconnect', (reason) => {
            console.log('BackendService - Disconnected:', reason);
            this.connected.next(false);
        });
        this.io.on('connect_error', (reason) => {
            console.error('BackendService - Connection error:', reason);
        });

        const auth: any = {};

        const url = window.location.href;
        const urlParam = new URL(url);
        if (
            urlParam &&
            urlParam.searchParams?.has('userId') &&
            urlParam.searchParams?.has('token') &&
            urlParam.searchParams?.has('code')
        ) {
            this.bridgeService.activateEmail = urlParam.searchParams?.has('activateEmail');
            this.bridgeService.platformUserId = urlParam.searchParams?.has('platformId')
                ? urlParam.searchParams.get('platformId')
                : null;
            if (this.lsIsAvailable) {
                localStorage.clear();
            }
        }

        if (this.lsIsAvailable) {
            auth.storage = window.localStorage;
        }

        this.client = feathers()
            .configure(socketIoClient(this.io, { timeout: 600000 }))
            .configure(authenticationClient({ storage: window.localStorage }));
    }

    public getService<T = any>(item: string): ServiceMethods<T> {
        return this.client.service(item);
    }

    public hasStorage() {
        try {
            localStorage.setItem('test', 'test');
            localStorage.removeItem('test');
            return true;
        } catch {
            return false;
        }
    }

    public setupHooks(hooks: any): void {
        this.client.hooks(hooks);
    }

    public async logout(): Promise<void> {
        await this.client.logout();
    }

    public authenticate(authentication?: any, params?: any): Promise<any> {
        return this.client.authenticate(authentication, params);
    }

    public reAuthenticate(force?: boolean, strategy?: string): Promise<any> {
        return this.client.reAuthenticate(force, strategy);
    }
}
